import {
	type ColumnDef,
	DataTable,
	FilterBuilder,
	type FilterField,
	SearchInput,
	TintedBackgroundIcon,
	useFilterBuilder,
	useUrlPagination,
} from "@monbits/ui";
import { Coins, DollarSign, FlaskConical } from "lucide-react";
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import { StatusBadge } from "src/components/status-badge";
import orderProvider, {
	type AdminOrder,
	type PaginatedOrdersResponse,
} from "src/providers/order-provider";
import {
	cryptoTickerOptions as CRYPTO_CURRENCY_OPTIONS,
	fiatOptions as FIAT_CURRENCY_OPTIONS,
} from "src/utils/options";
import {
	getStatusConfig as getUnifiedStatusConfig,
	STATUS_CONFIG,
} from "src/utils/status-config";

// Order status values to show in filter
const ORDER_STATUSES = [
	"pending",
	"partially_paid",
	"paid",
	"expired",
	"canceled",
	"aml_rejected",
] as const;

// Generate status options from unified config
const ORDER_STATUS_OPTIONS = ORDER_STATUSES.map((status) => {
	const config = STATUS_CONFIG[status];
	return {
		value: status,
		label: config.label,
		icon: (
			<TintedBackgroundIcon icon={config.icon} color={config.color} size="sm" />
		),
	};
});

const MODE_OPTIONS = [
	{ value: "false", label: "Live", color: "#22c55e" },
	{ value: "true", label: "Test", color: "#3b82f6" },
];

const FILTER_FIELDS: FilterField[] = [
	{
		id: "status",
		label: "Status",
		type: "select",
		icon: <STATUS_CONFIG.pending.icon className="h-4 w-4" />,
		options: ORDER_STATUS_OPTIONS,
	},
	{
		id: "fiatCurrency",
		label: "Fiat",
		type: "select",
		icon: <DollarSign className="h-4 w-4" />,
		options: FIAT_CURRENCY_OPTIONS,
	},
	{
		id: "cryptoCurrency",
		label: "Crypto",
		type: "select",
		icon: <Coins className="h-4 w-4" />,
		options: CRYPTO_CURRENCY_OPTIONS,
	},
	{
		id: "isTest",
		label: "Mode",
		type: "select",
		icon: <FlaskConical className="h-4 w-4" />,
		options: MODE_OPTIONS,
	},
];

interface OrdersListProps {
	orders: AdminOrder[];
	totalCount: number;
	isLoading: boolean;
	setOrders: React.Dispatch<React.SetStateAction<AdminOrder[]>>;
	setTotalCount: React.Dispatch<React.SetStateAction<number>>;
	setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const formatDate = (dateStr: string): string => {
	const date = new Date(dateStr);
	if (Number.isNaN(date.getTime())) {
		return dateStr;
	}
	return date.toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
};

const formatAmount = (value?: string | null): string => {
	if (!value) return "0";
	return value;
};

// Use unified status config
const getStatusConfig = (status: string) => {
	const config = getUnifiedStatusConfig(status);
	return {
		type: config.badgeType,
		icon: config.icon,
		label: config.label,
	};
};

const columns: ColumnDef<AdminOrder>[] = [
	{
		id: "merchant",
		header: "Merchant",
		cell: (row) => (
			<span className="font-medium">
				{row.merchantName}
				{row.isTest && (
					<StatusBadge
						type="info"
						className="ml-2 text-[10px] uppercase tracking-wide"
					>
						Test
					</StatusBadge>
				)}
			</span>
		),
	},
	{
		id: "uuid",
		header: "Order UUID",
		cell: (row) => <span className="font-mono text-xs">{row.uuid}</span>,
	},
	{
		id: "status",
		header: "Status",
		cell: (row) => {
			const config = getStatusConfig(row.status);
			return (
				<StatusBadge type={config.type} icon={config.icon}>
					{config.label}
				</StatusBadge>
			);
		},
	},
	{
		id: "currency",
		header: "Currency",
		cell: (row) => (
			<CryptoIcon
				ticker={row.cryptoCurrency}
				showLabel
				showComposite
				className="h-5 w-5"
			/>
		),
	},
	{
		id: "amount",
		header: "Amount",
		cell: (row) => (
			<span className="font-mono">
				{formatAmount(row.fiatAmount)} {row.fiatCurrency}
			</span>
		),
	},
	{
		id: "received",
		header: "Received",
		cell: (row) => (
			<span className="font-mono">{formatAmount(row.receivedAmount)}</span>
		),
	},
	{
		id: "created",
		header: "Created",
		cell: (row) => formatDate(row.createdAt),
	},
];

export function OrdersList({
	orders,
	totalCount,
	isLoading,
	setOrders,
	setTotalCount,
	setIsLoading,
}: OrdersListProps) {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const { filters, setFilters } = useFilterBuilder({
		fields: FILTER_FIELDS,
		searchParams,
		setSearchParams,
	});

	const search = searchParams.get("search") || "";
	const merchantId = searchParams.get("merchantId") || "";

	const { page, limit, offset, setPage, setLimit } = useUrlPagination({
		totalCount,
	});

	// Convert filter builder filters to API params
	const apiFilters = React.useMemo(() => {
		const result: {
			status?: string;
			fiatCurrency?: string;
			cryptoCurrency?: string;
			isTest?: boolean;
		} = {};

		for (const f of filters) {
			if (f.operator === "is") {
				if (f.field === "status") {
					result.status = f.value;
				} else if (f.field === "fiatCurrency") {
					result.fiatCurrency = f.value;
				} else if (f.field === "cryptoCurrency") {
					result.cryptoCurrency = f.value;
				} else if (f.field === "isTest") {
					result.isTest = f.value === "true";
				}
			}
		}

		return result;
	}, [filters]);

	const loadOrders = React.useCallback(async () => {
		try {
			setIsLoading(true);

			const response: PaginatedOrdersResponse = await orderProvider.listOrders({
				limit,
				offset,
				search,
				merchantId: merchantId ? Number(merchantId) : undefined,
				status: apiFilters.status,
				fiatCurrency: apiFilters.fiatCurrency,
				cryptoCurrency: apiFilters.cryptoCurrency,
				isTest: apiFilters.isTest,
			});

			setOrders(response.orders);
			setTotalCount(response.totalCount);
		} catch (error) {
			console.error("Failed to load orders:", error);
		} finally {
			setIsLoading(false);
		}
	}, [
		offset,
		limit,
		search,
		merchantId,
		apiFilters,
		setIsLoading,
		setOrders,
		setTotalCount,
	]);

	React.useEffect(() => {
		loadOrders();
	}, [loadOrders]);

	const handleRowClick = React.useCallback(
		(order: AdminOrder) => {
			const queryString = searchParams.toString();
			const targetPath = queryString
				? `/orders/${order.uuid}?${queryString}`
				: `/orders/${order.uuid}`;
			navigate(targetPath);
		},
		[navigate, searchParams],
	);

	return (
		<div className="p-4 sm:p-6 space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-3xl font-bold">Order Management</h1>
					<p className="text-sm text-muted-foreground mt-1">
						{totalCount} total order{totalCount !== 1 ? "s" : ""}
					</p>
				</div>
			</div>

			{merchantId && (
				<div className="flex items-center justify-between rounded-lg border bg-muted/40 px-4 py-2 text-sm">
					<span className="text-muted-foreground">
						Showing orders for one merchant.
					</span>
					<button
						type="button"
						className="text-primary hover:underline"
						onClick={() => {
							const next = new URLSearchParams(searchParams);
							next.delete("merchantId");
							setSearchParams(next);
						}}
					>
						Clear filter
					</button>
				</div>
			)}

			<div className="flex gap-4 flex-wrap items-center mb-4">
				<SearchInput
					placeholder="Search by merchant, order ID, or wallet..."
					paramName="search"
					className="w-[350px]"
				/>
				<FilterBuilder
					fields={FILTER_FIELDS}
					filters={filters}
					onFiltersChange={setFilters}
				/>
			</div>

			<DataTable
				columns={columns}
				data={orders}
				isLoading={isLoading}
				onRowClick={handleRowClick}
				getRowId={(row) => String(row.id)}
				emptyMessage={
					search || filters.length > 0
						? "No orders found. Try adjusting your search or filter criteria."
						: "No orders yet. Orders will appear here once they are created."
				}
				pagination={{
					page,
					pageSize: limit,
					totalCount,
					onPageChange: setPage,
					onPageSizeChange: setLimit,
				}}
			/>
		</div>
	);
}
